import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import FabBar from "@/design/components/FabBar";
import MasterListVirtualized from "@/design/components/MasterListVirtualized";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viBel, viPlus } from "@sippy-platform/valkyrie";

import { useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

function DistinctiveRingtoneList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const distinctiveRingtonesStore = useSelector((state) => state.domain.distinctiveRingtones);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  // Search every time lineKeyTemplatesList or searchQuery changes
  const searchResults = useSearch(distinctiveRingtonesStore.list, searchValue, ["name"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getDistinctiveRingtones()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!id && matchesMdUp && distinctiveRingtonesStore.meta.lastFetched) {
      if (distinctiveRingtonesStore.list.length >= 1) {
        navigate(`${distinctiveRingtonesStore.list[0].id}`);
      } else {
        navigate("new");
      }
    }
  }, [id, matchesMdUp, navigate, distinctiveRingtonesStore.list, distinctiveRingtonesStore.meta.lastFetched]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      <MasterListVirtualized
        fabBar
        title="Distinctive ringtones"
        aria-label="Distinctive ringtone list"
        id="distinctiveringtone-list"
        list={searchResults}
        listItemProps={{
          primary: (x) => x.name,
          secondary: (x) => x.exten,
          to: (x) => `../${x.id}`,
          selected: (x) => x.id === Number(id),
          icon: viBel,
        }}
        hideContentLoader={distinctiveRingtonesStore.meta.count >= 1 && searchResults.length >= 1}
        contentLoaderProps={{
          count: distinctiveRingtonesStore.meta.count,
          loading: progressBar,
          noItems: "You don't have any distinctive ringtones yet",
          noResults: "No distinctive ringtones in your current query",
          fetching: "Getting your distinctive ringtones",
          icon: viBel,
        }}
      />
      <FabBar>
        <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      </FabBar>
    </>
  );
}

export default DistinctiveRingtoneList;
